import React from "react";

interface ResponsiveContainerProps {
  children: React.ReactNode;
  className?: string;
  maxWidth?:
    | "xs"
    | "sm"
    | "md"
    | "lg"
    | "xl"
    | "2xl"
    | "3xl"
    | "4xl"
    | "5xl"
    | "6xl"
    | "7xl"
    | "full"
    | "none";
  padding?: boolean;
}

export function ResponsiveContainer({
  children,
  className = "",
  maxWidth = "xl",
  padding = true,
}: ResponsiveContainerProps) {
  const maxWidthClasses = {
    xs: "max-w-xs",
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    "2xl": "max-w-2xl",
    "3xl": "max-w-3xl",
    "4xl": "max-w-4xl",
    "5xl": "max-w-5xl",
    "6xl": "max-w-6xl",
    "7xl": "max-w-7xl",
    full: "max-w-full",
    none: "",
  };

  return (
    <div
      className={`w-full mx-auto ${maxWidthClasses[maxWidth]} ${
        padding ? "px-2 sm:px-4" : ""
      } ${className}`}
    >
      {children}
    </div>
  );
}

interface ResponsiveGridProps {
  children: React.ReactNode;
  columns?: {
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
  gap?: "none" | "sm" | "md" | "lg";
  className?: string;
}

export function ResponsiveGrid({
  children,
  columns = { sm: 1, md: 2, lg: 3, xl: 4 },
  gap = "md",
  className = "",
}: ResponsiveGridProps) {
  const gapClasses = {
    none: "gap-0",
    sm: "gap-2",
    md: "gap-4",
    lg: "gap-6",
  };

  const getGridCols = () => {
    const { sm = 1, md = 2, lg = 3, xl = 4 } = columns;
    return `grid-cols-1 sm:grid-cols-${sm} md:grid-cols-${md} lg:grid-cols-${lg} xl:grid-cols-${xl}`;
  };

  return (
    <div className={`grid ${getGridCols()} ${gapClasses[gap]} ${className}`}>
      {children}
    </div>
  );
}

interface ResponsiveTwoColumnLayoutProps {
  children: React.ReactNode;
  sidebar: React.ReactNode;
  sidebarWidth?: string;
  sidebarPosition?: "left" | "right";
  className?: string;
  sidebarClassName?: string;
  contentClassName?: string;
}

export function ResponsiveTwoColumnLayout({
  children,
  sidebar,
  sidebarWidth = "300px",
  sidebarPosition = "left",
  className = "",
  sidebarClassName = "",
  contentClassName = "",
}: ResponsiveTwoColumnLayoutProps) {
  return (
    <div className={`flex flex-col md:flex-row w-full h-full ${className}`}>
      {sidebarPosition === "left" && (
        <div
          className={`md:w-auto md:flex-shrink-0 ${sidebarClassName}`}
          style={{ width: sidebarWidth }}
        >
          {sidebar}
        </div>
      )}

      <div className={`flex-1 ${contentClassName}`}>{children}</div>

      {sidebarPosition === "right" && (
        <div
          className={`md:w-auto md:flex-shrink-0 ${sidebarClassName}`}
          style={{ width: sidebarWidth }}
        >
          {sidebar}
        </div>
      )}
    </div>
  );
}
